// src/pages/Conversations.jsx — Conversas v2 (mockup-aligned, light content)
//
// Mantemos AppSidebar e TopBar (ambos dark) como estão — o redesign vale
// só pra área de conteúdo, que vira light mode com a paleta enviada
// pelo usuário (#F0F3F8 bg, #FFFFFF cards, tons brand verde saturado).
//
// Adições da v2:
//   - Page header com eyebrow + título + subtítulo + pills de status
//     (X online / Plano + X/Y chips / Y offline).
//   - Toolbar com busca (atalho ⌘K visual) + filtros (Todos/Online/
//     Atenção/Offline) + sort (saúde/nome/msgs).
//   - Section label com legenda inline de cores e view-toggle grade/lista
//     (lista ainda não implementada — botão fica visual).
//   - Card de chip novo: avatar com inicial + ponto de status overlay,
//     badges (Conectado/Plano), barra de saúde gradiente, grid de 3 stats
//     (Msgs hoje / Limite / Uso), divider, footer com preview da última
//     mensagem (vem do novo campo last_message no /devices/) e CTAs por
//     estado (Online→Gerenciar/Abrir chat, Atenção→Pausar/Abrir,
//     Offline→Reconectar).
//   - Bloco "Entendendo a saúde do chip" no rodapé com guia 80-100/50-79/
//     0-49%.
//
// Limite de plano: continua usando usePlanUsage. Quando chip is na 1 (STARTER)
// ou ENTERPRISE 9999, mostramos na pill superior.
import { useEffect, useState, useMemo } from 'react';
import { Box, Typography, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton, CircularProgress, Tooltip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  FiSearch, FiTrash2, FiCheckCircle, FiWifi, FiWifiOff, FiAlertTriangle,
  FiGrid, FiList, FiArrowRight, FiPause, FiSettings, FiRefreshCw,
  FiLock,
} from 'react-icons/fi';
import api from '../services/Api';
import AppSidebar from '../components/AppSidebar';
import TopBar from '../components/TopBar';
import usePlanUsage from '../hooks/usePlanUsage';

// ─── Paleta light do mockup ─────────────────────────────────────────────────
const C = {
  bg:        '#F0F3F8',
  card:      '#FFFFFF',
  surf:      '#F7F9FC',
  inset:     '#EEF1F7',
  bdr:       '#E2E8F0',
  bdr2:      '#CBD5E1',

  green:     '#00B865',
  greenD:    '#006B3F',
  greenBg:   '#E6FAF2',
  greenB:    '#A7F0D2',
  greenGlow: 'rgba(0,184,101,0.25)',

  amber:     '#D97706',
  amberD:    '#92400E',
  amberBg:   '#FFFBEB',
  amberB:    '#FDE68A',

  red:       '#DC2626',
  redD:      '#7F1D1D',
  redBg:     '#FEF2F2',
  redB:      '#FECACA',

  blue:      '#2563EB',
  blueD:     '#1E40AF',
  blueBg:    '#EFF6FF',
  blueB:     '#BFDBFE',

  purple:    '#7C3AED',
  purpBg:    '#F5F3FF',
  purpB:     '#DDD6FE',

  text:      '#1A2535',
  text2:     '#4B5E72',
  text3:     '#94A3B8',
};
const FONT_TXT = "'Syne', sans-serif";
const FONT_NUM = "'Outfit', sans-serif";

const SHADOW_SM = '0 1px 3px rgba(15,28,50,0.08), 0 1px 2px rgba(15,28,50,0.05)';
const SHADOW_MD = '0 4px 16px rgba(15,28,50,0.11), 0 2px 4px rgba(15,28,50,0.06)';

// ─── Helpers ────────────────────────────────────────────────────────────────

// Normaliza o status do device (backend usa enum UPPERCASE) e devolve a
// "categoria visual" usada pelo card (ok / warn / risk / off).
function classifyChip(device) {
  const status = String(device.status || '').toUpperCase();
  const health = device.health_score ?? 100;
  if (status !== 'ONLINE') return 'off';      // offline trump anything
  if (health < 50) return 'risk';
  if (health < 80) return 'warn';
  return 'ok';
}

// Formato amigável para "há X tempo atrás" sem dependência externa.
function timeAgo(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return `${diff}s atrás`;
  if (diff < 3600) return `${Math.floor(diff / 60)} min atrás`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h atrás`;
  const days = Math.floor(diff / 86400);
  return `há ${days} dia${days !== 1 ? 's' : ''}`;
}

// ─── Componentes auxiliares ─────────────────────────────────────────────────

function Pill({ tone, children }) {
  // tone: 'green' | 'blue' | 'amber'
  const palette = {
    green: { bg: C.greenBg, color: C.greenD, border: C.greenB },
    blue:  { bg: C.blueBg,  color: C.blueD,  border: C.blueB  },
    amber: { bg: C.amberBg, color: C.amberD, border: C.amberB },
  }[tone] || { bg: C.surf, color: C.text2, border: C.bdr };
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.6,
      px: 1.5, py: 0.5, borderRadius: '20px',
      fontSize: '0.7rem', fontWeight: 600,
      bgcolor: palette.bg, color: palette.color,
      border: `1px solid ${palette.border}`,
      fontFamily: FONT_TXT,
    }}>
      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'currentColor' }} />
      {children}
    </Box>
  );
}

function Badge({ tone, children }) {
  const palette = {
    conn:  { bg: C.greenBg, color: C.greenD, border: C.greenB },
    off:   { bg: C.inset,   color: C.text3,  border: C.bdr2   },
    warn:  { bg: C.amberBg, color: C.amberD, border: C.amberB },
    risk:  { bg: C.redBg,   color: C.redD,   border: C.redB   },
    ent:   { bg: C.purpBg,  color: C.purple, border: C.purpB  },
    bus:   { bg: C.blueBg,  color: C.blueD,  border: C.blueB  },
  }[tone] || { bg: C.inset, color: C.text2, border: C.bdr };
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.4,
      px: 0.9, py: 0.3, borderRadius: '20px',
      fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.06em',
      textTransform: 'uppercase',
      bgcolor: palette.bg, color: palette.color,
      border: `1px solid ${palette.border}`,
      fontFamily: FONT_TXT,
    }}>
      {children}
    </Box>
  );
}

// ─── Os dois limites do chip ────────────────────────────────────────────────
//
// Out/26. A Pontal Seafood perguntou: "por que aponta 500 se eu só posso
// enviar 250?". Eram dois limites diferentes, e o card mostrava só o nosso:
//
//   PROTEÇÃO CETTO  freio anti-abuso do plano. Conta TEMPLATES ENVIADOS no
//                   dia (fuso de Brasília). É nosso, existe pra frear
//                   disparo em massa que queima o número na Meta.
//   LIMITE META     o tier do número. Conta CONTATOS NOVOS iniciados em 24h
//                   corridas, não templates. É da Meta, é quem realmente
//                   barra o envio, e não temos controle sobre ele.
//
// Unidades diferentes, donos diferentes, janelas diferentes — por isso não
// dá pra somar nem mostrar um só. O acento azul à esquerda marca a linha da
// Meta como EXTERNA: é o que ela não controla e nós não aplicamos.
function LimitesDoChip({ templatesHoje, capCetto, tierMeta }) {
  const pct = capCetto ? Math.min(100, (templatesHoje / capCetto) * 100) : 0;
  const corBarra = pct > 80 ? C.amber : pct > 0 ? C.green : C.bdr2;
  const temTier = tierMeta && tierMeta !== 'UNKNOWN';

  const Linha = ({ rotulo, valor, unidade, acento, children }) => (
    <Box sx={{
      display: 'flex', alignItems: 'baseline', gap: 1.2,
      px: 1.1, py: 0.9,
      borderLeft: `2px solid ${acento || 'transparent'}`,
    }}>
      <Typography sx={{
        fontFamily: FONT_TXT, fontSize: '0.56rem', fontWeight: 700,
        letterSpacing: '0.1em', textTransform: 'uppercase', color: C.text3,
        // Largura pro maior rótulo ("Proteção Cetto") caber numa linha só:
        // com um quebrando e o outro não, as duas faixas ficavam com
        // alturas diferentes e pareciam desalinhadas. Em tela estreita
        // quebra mesmo, e aí quebra nas duas.
        width: 92, flexShrink: 0, lineHeight: 1.3,
      }}>
        {rotulo}
      </Typography>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{
          fontFamily: FONT_NUM, fontSize: '0.9rem', fontWeight: 700,
          color: C.text, lineHeight: 1.1,
        }}>
          {valor}
        </Typography>
        <Typography sx={{
          fontFamily: FONT_TXT, fontSize: '0.6rem', color: C.text3,
          lineHeight: 1.4, mt: 0.1,
        }}>
          {unidade}
        </Typography>
        {children}
      </Box>
    </Box>
  );

  return (
    <Box sx={{
      bgcolor: C.surf, border: `1px solid ${C.bdr}`,
      borderRadius: '6px', overflow: 'hidden', mb: 1.5,
    }}>
      <Linha
        rotulo="Proteção Cetto"
        valor={
          <>
            {templatesHoje.toLocaleString('pt-BR')}
            <Box component="span" sx={{ color: C.text3, fontWeight: 400 }}>
              {' / '}{capCetto ? capCetto.toLocaleString('pt-BR') : '∞'}
            </Box>
          </>
        }
        unidade="templates hoje · zera à meia-noite"
      >
        {capCetto > 0 && (
          <Box sx={{ height: 3, bgcolor: C.inset, borderRadius: '2px', mt: 0.6 }}>
            <Box sx={{
              width: `${pct}%`, height: '100%', bgcolor: corBarra,
              borderRadius: '2px', transition: 'width 1s ease',
            }} />
          </Box>
        )}
      </Linha>
      <Box sx={{ borderTop: `1px solid ${C.bdr}` }} />
      <Linha
        rotulo="Limite Meta"
        acento={C.blue}
        valor={temTier ? tierMeta.replace(/^TIER_/, '') : '—'}
        unidade={temTier
          ? 'contatos novos por 24h — limite da Meta'
          : 'a Meta ainda não informou o tier deste chip'}
      />
    </Box>
  );
}

function FilterButton({ active, onClick, children }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.6,
        px: 1.4, py: 0.9, borderRadius: '6px',
        fontSize: '0.7rem', fontWeight: 600, fontFamily: FONT_TXT,
        cursor: 'pointer',
        border: `1px solid ${active ? C.greenB : C.bdr}`,
        bgcolor: active ? C.greenBg : C.card,
        color: active ? C.greenD : C.text2,
        boxShadow: SHADOW_SM,
        transition: 'all 0.15s',
        '&:hover': !active && {
          borderColor: C.bdr2,
          color: C.text,
        }
      }}
    >
      {children}
    </Box>
  );
}

// ─── Componente principal ──────────────────────────────────────────────────

export default function Conversations() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('all'); // all | online | warn | off
  const [sortBy, setSortBy] = useState('health');           // health | name | msgs
  const [viewMode, setViewMode] = useState('grid');         // grid | list (placeholder)

  // Exclusão
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Limites do plano
  const { usage, refetch: refetchUsage } = usePlanUsage();
  const canAddChip = !!usage?.chips?.can_add;
  const isSupervisor = user?.role === 'SUPERVISOR' || user?.role === 'ADMIN';

  // ── Fetch loop ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    const fetchDevices = async () => {
      try {
        const res = await api.get('/devices/', {
          headers: { Authorization: `Bearer ${token}` }
        });
        setDevices(res.data);
      } catch (err) {
        console.error("Erro ao carregar chips:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchDevices();
    const interval = setInterval(fetchDevices, 20000);
    return () => clearInterval(interval);
  }, [token, navigate]);

  // ── Excluir chip ────────────────────────────────────────────────────────
  const handleDeleteDevice = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      await api.delete(`/devices/${deleteConfirm.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDevices(prev => prev.filter(d => d.id !== deleteConfirm.id));
      setDeleteConfirm(null);
      refetchUsage();
    } catch (err) {
      alert('Erro ao excluir chip: ' + (err.response?.data?.detail || 'Tente novamente.'));
    } finally {
      setDeleting(false);
    }
  };

  // ── Derivados ───────────────────────────────────────────────────────────
  const filteredDevices = useMemo(() => {
    let list = devices.filter(d =>
      d.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.phone_number?.includes(searchTerm)
    );
    if (activeFilter === 'online') list = list.filter(d => classifyChip(d) === 'ok');
    if (activeFilter === 'warn')   list = list.filter(d => ['warn', 'risk'].includes(classifyChip(d)));
    if (activeFilter === 'off')    list = list.filter(d => classifyChip(d) === 'off');

    if (sortBy === 'name')   list = [...list].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    if (sortBy === 'msgs')   list = [...list].sort((a, b) => (b.messages_today || 0) - (a.messages_today || 0));
    if (sortBy === 'health') list = [...list].sort((a, b) => (b.health_score ?? 100) - (a.health_score ?? 100));

    return list;
  }, [devices, searchTerm, activeFilter, sortBy]);

  const counts = useMemo(() => {
    const c = { all: devices.length, online: 0, warn: 0, off: 0 };
    devices.forEach(d => {
      const cls = classifyChip(d);
      if (cls === 'ok')   c.online++;
      if (cls === 'warn' || cls === 'risk') c.warn++;
      if (cls === 'off')  c.off++;
    });
    return c;
  }, [devices]);

  // ── Tema de cores por categoria do chip ─────────────────────────────────
  const themeFor = (cls) => ({
    ok:   { topBdr: C.green, avBg: C.greenBg, avColor: C.greenD, avBdr: C.greenB, dot: C.green,  hpct: C.greenD, hbarFrom: C.green,  hbarTo: 'rgba(0,184,101,0.4)' },
    warn: { topBdr: C.amber, avBg: C.amberBg, avColor: C.amberD, avBdr: C.amberB, dot: C.amber,  hpct: C.amberD, hbarFrom: C.amber,  hbarTo: 'rgba(217,119,6,0.4)' },
    risk: { topBdr: C.red,   avBg: C.redBg,   avColor: C.redD,   avBdr: C.redB,   dot: C.red,    hpct: C.redD,   hbarFrom: C.red,    hbarTo: 'rgba(220,38,38,0.4)' },
    off:  { topBdr: C.text3, avBg: C.inset,   avColor: C.text3,  avBdr: C.bdr2,   dot: C.text3,  hpct: C.text3,  hbarFrom: C.bdr2,   hbarTo: C.bdr2 },
  }[cls]);

  const planLimitChips = usage?.chips?.limit ?? '?';
  const planName = usage?.plan ?? '—';

  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: C.bg, fontFamily: FONT_TXT, color: C.text, overflow: 'hidden' }}>
      {/* Sidebar dark — inalterada */}
      <AppSidebar user={user} />

      {/* Main column */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          ml: { xs: 0, md: 'var(--sidebar-width, 260px)' },
          transition: 'margin-left 0.25s ease',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* TopBar dark — inalterada (com botão Conectar Chip se supervisor + can_add) */}
        <TopBar
          crumbBase="Navegação"
          crumbCur="Conversas"
          canAddChip={isSupervisor && canAddChip}
          onAddChip={() => navigate('/dashboard')}
        />

        {/* Conteúdo rolável light */}
        <Box sx={{
          flex: 1, overflowY: 'auto',
          p: { xs: 2.5, md: '24px 26px 32px' },
          display: 'flex', flexDirection: 'column', gap: 2.5,
          bgcolor: C.bg,
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-thumb': { background: C.bdr2, borderRadius: 4 },
        }}>

          {/* ═══ PAGE HEADER ═══ */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2.5, flexWrap: 'wrap' }}>
            <Box>
              <Box sx={{
                fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.18em',
                textTransform: 'uppercase', color: C.text3, mb: 0.5,
                display: 'flex', alignItems: 'center', gap: 0.8,
                '&::before': { content: '""', display: 'inline-block', width: 14, height: 1, bgcolor: C.text3 }
              }}>
                Gerenciamento de Chips
              </Box>
              <Typography sx={{
                fontSize: '1.65rem', fontWeight: 800, color: C.text,
                letterSpacing: '-0.5px', mb: 0.5, lineHeight: 1.1,
                fontFamily: FONT_TXT,
              }}>
                Conversas
              </Typography>
              <Typography sx={{ fontSize: '0.85rem', color: C.text2, fontFamily: FONT_TXT }}>
                Selecione um chip oficial para iniciar ou gerenciar atendimentos.
              </Typography>

              {/* Pills de status */}
              <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                <Pill tone="green">{counts.online} chip{counts.online !== 1 ? 's' : ''} online</Pill>
                {usage && (
                  <Pill tone="blue">
                    Plano {planName} · {counts.all} / {planLimitChips} chips
                  </Pill>
                )}
                {counts.off > 0 && (
                  <Pill tone="amber">⚠ {counts.off} chip{counts.off !== 1 ? 's' : ''} offline</Pill>
                )}
              </Box>
            </Box>

            {/* Toolbar à direita: busca + filtros + sort */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-end' }}>
              {/* Search */}
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1,
                bgcolor: C.card, border: `1px solid ${C.bdr}`,
                borderRadius: '6px', px: 1.5, py: 1,
                width: { xs: '100%', md: 280 },
                boxShadow: SHADOW_SM,
                transition: 'border-color 0.2s',
                '&:focus-within': { borderColor: C.greenB }
              }}>
                <FiSearch size={13} color={C.text3} />
                <Box
                  component="input"
                  type="text"
                  placeholder="Buscar por nome ou número…"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  sx={{
                    flex: 1, background: 'none', border: 'none', outline: 'none',
                    fontFamily: FONT_TXT, fontSize: '0.78rem', color: C.text,
                    '&::placeholder': { color: C.text3 }
                  }}
                />
                <Box sx={{
                  fontFamily: FONT_NUM, fontSize: '0.62rem', color: C.text3,
                  bgcolor: C.inset, px: 0.7, py: 0.1, borderRadius: '4px',
                  border: `1px solid ${C.bdr}`,
                }}>⌘K</Box>
              </Box>

              {/* Filtros + sort */}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <FilterButton active={activeFilter === 'all'}    onClick={() => setActiveFilter('all')}>Todos ({counts.all})</FilterButton>
                <FilterButton active={activeFilter === 'online'} onClick={() => setActiveFilter('online')}>Online ({counts.online})</FilterButton>
                <FilterButton active={activeFilter === 'warn'}   onClick={() => setActiveFilter('warn')}>Atenção ({counts.warn})</FilterButton>
                {counts.off > 0 && (
                  <FilterButton active={activeFilter === 'off'} onClick={() => setActiveFilter('off')}>Offline ({counts.off})</FilterButton>
                )}
                <Box
                  component="select"
                  value={sortBy}
                  onChange={e => setSortBy(e.target.value)}
                  sx={{
                    px: 1.4, py: 0.9, borderRadius: '6px',
                    fontFamily: FONT_TXT, fontSize: '0.7rem', fontWeight: 600,
                    border: `1px solid ${C.bdr}`, bgcolor: C.card, color: C.text2,
                    cursor: 'pointer', outline: 'none', boxShadow: SHADOW_SM,
                  }}
                >
                  <option value="health">Ordenar: Saúde</option>
                  <option value="name">Ordenar: Nome</option>
                  <option value="msgs">Ordenar: Msgs hoje</option>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* ═══ SECTION LABEL + LEGENDA + VIEW TOGGLE ═══ */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: -1.2, flexWrap: 'wrap', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography sx={{ fontSize: '0.66rem', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: C.text3, fontFamily: FONT_TXT }}>
                Chips ativos
              </Typography>
              <Box sx={{
                fontFamily: FONT_NUM, fontSize: '0.62rem', color: C.text3,
                bgcolor: C.inset, px: 1, py: 0.1, borderRadius: '20px',
                border: `1px solid ${C.bdr}`,
              }}>
                {filteredDevices.length} chip{filteredDevices.length !== 1 ? 's' : ''}
              </Box>
              {/* Legenda */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ml: 1.5, flexWrap: 'wrap' }}>
                {[
                  { color: C.green, label: 'Saudável' },
                  { color: C.amber, label: 'Atenção' },
                  { color: C.red,   label: 'Risco' },
                  { color: C.text3, label: 'Offline' },
                ].map(item => (
                  <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: item.color }} />
                    <Typography sx={{ fontSize: '0.68rem', color: C.text3, fontFamily: FONT_TXT }}>{item.label}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* View toggle (grid/list - list é placeholder por enquanto) */}
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {[
                { mode: 'grid', icon: FiGrid },
                { mode: 'list', icon: FiList },
              ].map(({ mode, icon: Icon }) => (
                <Box
                  key={mode}
                  onClick={() => setViewMode(mode)}
                  title={mode === 'grid' ? 'Grade' : 'Lista'}
                  sx={{
                    width: 28, height: 28,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    borderRadius: '6px', cursor: 'pointer',
                    border: `1px solid ${viewMode === mode ? C.greenB : C.bdr}`,
                    bgcolor: viewMode === mode ? C.greenBg : C.card,
                    color: viewMode === mode ? C.greenD : C.text3,
                    transition: 'all 0.15s',
                  }}
                >
                  <Icon size={13} />
                </Box>
              ))}
            </Box>
          </Box>

          {/* ═══ CHIPS GRID ═══ */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress sx={{ color: C.green }} />
            </Box>
          ) : filteredDevices.length === 0 ? (
            <Box sx={{
              bgcolor: C.card, border: `1px dashed ${C.bdr2}`, borderRadius: '10px',
              p: 6, textAlign: 'center', boxShadow: SHADOW_SM,
            }}>
              <FiSearch size={40} color={C.text3} />
              <Typography sx={{ color: C.text2, mt: 2, fontWeight: 600, fontFamily: FONT_TXT }}>
                Nenhum chip encontrado{searchTerm ? ` para "${searchTerm}"` : ''}.
              </Typography>
              <Typography sx={{ color: C.text3, mt: 0.5, fontSize: '0.78rem', fontFamily: FONT_TXT }}>
                Ajuste os filtros ou conecte um chip oficial pelo botão "Conectar Chip" no topo.
              </Typography>
            </Box>
          ) : (
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
              gap: 1.8,
            }}>
              {filteredDevices.map((device, idx) => {
                const cls = classifyChip(device);
                const t = themeFor(cls);
                const health = device.health_score ?? 100;
                const initial = (device.name || '?')[0]?.toUpperCase();
                // Cap diário por chip é RATE-LIMIT ANTI-ABUSO (STARTER=500,
                // BUSINESS=1000, ENTERPRISE=5000 — ver plan_limits.py). Só
                // se aplica a template; msg freeform dentro da janela 24h
                // é grátis e sem cap. Por isso o card mostra
                // `templates_today` e não `messages_today`. Cap alto
                // porque Meta cobra template direto do cliente — nosso
                // limite existe pra frear bot/spam, não pra vender pack.
                const templatesToday = device.templates_today || 0;
                const dailyCap = usage?.daily_cap_per_device ?? null;
                const last = device.last_message;
                const ago = timeAgo(last?.timestamp);

                // CTAs por estado
                const ctas = cls === 'off' ? (
                  <Box
                    component="button"
                    sx={ctaStyle(C.surf, C.text2, C.bdr)}
                  >
                    <FiRefreshCw size={11} />
                    Reconectar
                  </Box>
                ) : cls === 'warn' || cls === 'risk' ? (
                  <>
                    <Box component="button" sx={ctaStyle(C.amberBg, C.amberD, C.amberB)}>
                      <FiPause size={11} />
                      Pausar chip
                    </Box>
                    <Box
                      component="button"
                      onClick={() => navigate(`/chat/${device.id}`)}
                      sx={ctaPrimary}
                    >
                      Abrir chat
                      <FiArrowRight size={11} />
                    </Box>
                  </>
                ) : (
                  <>
                    <Box
                      component="button"
                      onClick={(e) => { e.stopPropagation(); navigate('/admin/chips'); }}
                      sx={ctaStyle(C.surf, C.text2, C.bdr)}
                    >
                      <FiSettings size={11} />
                      Gerenciar
                    </Box>
                    <Box
                      component="button"
                      onClick={() => navigate(`/chat/${device.id}`)}
                      sx={ctaPrimary}
                    >
                      Abrir chat
                      <FiArrowRight size={11} />
                    </Box>
                  </>
                );

                return (
                  <Box
                    key={device.id}
                    onClick={() => cls !== 'off' && navigate(`/chat/${device.id}`)}
                    sx={{
                      bgcolor: C.card, border: `1px solid ${C.bdr}`,
                      borderRadius: '10px', overflow: 'hidden',
                      boxShadow: SHADOW_SM,
                      cursor: cls === 'off' ? 'default' : 'pointer',
                      transition: 'all 0.2s',
                      borderTop: `3px solid ${t.topBdr}`,
                      opacity: cls === 'off' ? 0.78 : 1,
                      position: 'relative',
                      animation: `chipIn 0.3s ease both`,
                      animationDelay: `${idx * 0.04}s`,
                      '@keyframes chipIn': {
                        from: { opacity: 0, transform: 'translateY(9px)' },
                        to:   { opacity: cls === 'off' ? 0.78 : 1, transform: 'translateY(0)' },
                      },
                      '&:hover': {
                        boxShadow: SHADOW_MD,
                        borderColor: C.bdr2,
                        transform: cls === 'off' ? 'none' : 'translateY(-2px)',
                      },
                      '&:hover .delete-btn': { opacity: 1 },
                    }}
                  >
                    <Box sx={{ p: '16px 17px 14px' }}>
                      {/* Top: avatar + meta + delete */}
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
                        <Box sx={{
                          width: 42, height: 42, borderRadius: '50%',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '1rem', fontWeight: 800, fontFamily: FONT_TXT,
                          flexShrink: 0, position: 'relative',
                          bgcolor: t.avBg, color: t.avColor,
                          border: `1.5px solid ${t.avBdr}`,
                        }}>
                          {initial}
                          {/* Status dot overlay */}
                          <Box sx={{
                            width: 10, height: 10, borderRadius: '50%',
                            position: 'absolute', bottom: 0, right: 0,
                            border: `2px solid ${C.card}`,
                            bgcolor: t.dot,
                            boxShadow: cls === 'ok' ? `0 0 5px ${C.greenGlow}` : 'none',
                            animation: cls === 'ok' ? 'dotPulse 2.5s infinite' : 'none',
                            '@keyframes dotPulse': {
                              '0%, 100%': { boxShadow: `0 0 0 0 ${C.greenGlow}` },
                              '50%':      { boxShadow: '0 0 0 5px rgba(0,184,101,0)' },
                            }
                          }} />
                        </Box>

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 0.2 }}>
                            <Typography sx={{
                              fontSize: '0.82rem', fontWeight: 700, color: C.text,
                              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                              fontFamily: FONT_TXT,
                            }}>
                              {device.name}
                            </Typography>
                            {device.phone_number_id && (
                              <FiCheckCircle size={12} color={cls === 'off' ? C.text3 : C.green} />
                            )}
                          </Box>
                          <Typography sx={{
                            fontFamily: FONT_NUM, fontSize: '0.7rem', color: C.text3,
                            letterSpacing: '0.04em',
                          }}>
                            +{device.phone_number}
                          </Typography>
                          {/* Badges */}
                          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.7, flexWrap: 'wrap' }}>
                            {cls === 'ok' && <Badge tone="conn">● Conectado</Badge>}
                            {cls === 'warn' && <Badge tone="warn">⚡ Atenção</Badge>}
                            {cls === 'risk' && <Badge tone="risk">⚠ Risco</Badge>}
                            {cls === 'off' && <Badge tone="off">● Offline</Badge>}
                            {/* Quality Rating oficial Meta — só mostra quando não é UNKNOWN
                                (chip ainda não passou por evento phone_number_quality_update).
                                O tooltip revela o messaging_limit tier. */}
                            {device.quality_rating && device.quality_rating !== 'UNKNOWN' && (
                              <Tooltip
                                title={`Quality oficial Meta: ${device.quality_rating}${
                                  device.messaging_limit && device.messaging_limit !== 'UNKNOWN'
                                    ? ` · Tier ${device.messaging_limit}`
                                    : ''
                                }`}
                              >
                                <span>
                                  <Badge
                                    tone={
                                      device.quality_rating === 'GREEN' ? 'conn'
                                      : device.quality_rating === 'YELLOW' ? 'warn'
                                      : 'risk'
                                    }
                                  >
                                    Meta {device.quality_rating}
                                  </Badge>
                                </span>
                              </Tooltip>
                            )}
                            {/* Status Meta consolidado (BV + WABA review + code verify).
                                Só mostra quando não é UNKNOWN (backend faz refresh lazy). */}
                            {device.meta_status?.overall && device.meta_status.overall !== 'UNKNOWN' && (() => {
                              const s = device.meta_status.overall;
                              const map = {
                                APPROVED:         { label: '✓ Aprovado',      tone: 'conn' },
                                PENDING:          { label: '◐ Pendente',       tone: 'warn' },
                                REJECTED:         { label: '✕ Rejeitado',     tone: 'risk' },
                                CODE_NOT_VERIFIED:{ label: '⚠ Verifique código', tone: 'warn' },
                              };
                              const cfg = map[s];
                              if (!cfg) return null;
                              const d = device.meta_status.detail || {};
                              return (
                                <Tooltip title={`BV: ${d.bv_status} · WABA: ${d.waba_review_status} · Código: ${d.code_verification_status}`}>
                                  <span><Badge tone={cfg.tone}>{cfg.label}</Badge></span>
                                </Tooltip>
                              );
                            })()}
                            <Badge tone={planName === 'ENTERPRISE' ? 'ent' : 'bus'}>{planName}</Badge>
                          </Box>
                        </Box>

                        {/* Delete (hover) */}
                        <IconButton
                          className="delete-btn"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteConfirm(device);
                          }}
                          sx={{
                            opacity: { xs: 1, md: 0 },
                            transition: 'opacity 0.2s, color 0.2s',
                            color: C.text3,
                            '&:hover': { color: C.red, bgcolor: C.redBg }
                          }}
                        >
                          <FiTrash2 size={13} />
                        </IconButton>
                      </Box>

                      {/* Health */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.8 }}>
                        <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: C.text3, fontFamily: FONT_TXT }}>
                          Saúde do chip
                        </Typography>
                        <Typography sx={{ fontFamily: FONT_NUM, fontSize: '0.85rem', fontWeight: 700, color: t.hpct }}>
                          {health}%
                        </Typography>
                      </Box>
                      <Box sx={{ height: 5, bgcolor: C.inset, borderRadius: '3px', overflow: 'hidden', mb: 1.5 }}>
                        <Box sx={{
                          height: '100%', width: `${health}%`,
                          background: cls === 'off'
                            ? C.bdr2
                            : `linear-gradient(90deg, ${t.hbarFrom}, ${t.hbarTo})`,
                          borderRadius: '3px',
                          transition: 'width 1s ease',
                        }} />
                      </Box>

                      {/* Os dois limites, lado a lado e rotulados.
                          Antes eram 3 caixinhas — "Templates hoje", "Cap
                          diário" e "Uso" — que mostravam só o limite NOSSO,
                          sem dizer que era nosso. A cliente leu o 500 como
                          cota de envio e estranhou quando a Meta a barrou
                          em 250, que é outro limite, de outra unidade. */}
                      <LimitesDoChip
                        templatesHoje={templatesToday}
                        capCetto={dailyCap}
                        tierMeta={device.messaging_limit}
                      />

                      <Box sx={{ borderTop: `1px solid ${C.bdr}`, mb: 1.3 }} />

                      {/* Footer: preview + CTAs */}
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.1, minWidth: 0, flex: 1 }}>
                          <Typography sx={{ fontSize: '0.62rem', color: C.text3, fontFamily: FONT_TXT }}>
                            {cls === 'off'
                              ? `Desconectado · ${ago || 'há tempos'}`
                              : ago ? `Última msg · ${ago}` : 'Nenhuma mensagem ainda'}
                          </Typography>
                          <Typography sx={{
                            fontSize: '0.7rem', fontFamily: FONT_TXT,
                            color: cls === 'warn' || cls === 'risk' ? C.amberD
                                 : cls === 'off' ? C.text3
                                 : C.text2,
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                          }}>
                            {cls === 'off'
                              ? 'Chip aguardando reconexão'
                              : cls === 'warn' || cls === 'risk'
                                ? '⚠ Recomendado: reduzir envios'
                                : last
                                  ? <>💬 "{last.content}"</>
                                  : 'Aguardando primeira mensagem'}
                          </Typography>
                        </Box>
                        <Box
                          onClick={e => e.stopPropagation()}
                          sx={{ display: 'flex', gap: 0.7, flexShrink: 0 }}
                        >
                          {ctas}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}

          {/* ═══ HEALTH GUIDE ═══ */}
          <Box sx={{
            bgcolor: C.card, border: `1px solid ${C.bdr}`, borderRadius: '10px',
            p: '16px 20px', boxShadow: SHADOW_SM,
          }}>
            <Typography sx={{
              fontSize: '0.66rem', fontWeight: 700, letterSpacing: '0.14em',
              textTransform: 'uppercase', color: C.text3, mb: 1.5, fontFamily: FONT_TXT,
            }}>
              Entendendo a saúde do chip
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 1.5 }}>
              {[
                { color: C.green, range: '80–100% · Saudável',  desc: 'Chip em plena capacidade. Sem risco de bloqueio.' },
                { color: C.amber, range: '50–79% · Atenção',     desc: 'Volume elevado ou padrão atípico. Considere pausa preventiva.' },
                { color: C.red,   range: '0–49% · Risco',        desc: 'Bloqueio iminente. A Cetto aciona blindagem ativa automaticamente.' },
              ].map(item => (
                <Box key={item.range} sx={{ display: 'flex', gap: 1.2, alignItems: 'flex-start' }}>
                  <Box sx={{
                    width: 8, height: 8, borderRadius: '50%',
                    bgcolor: item.color, mt: 0.6, flexShrink: 0,
                  }} />
                  <Box>
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: C.text, mb: 0.2, fontFamily: FONT_TXT }}>
                      {item.range}
                    </Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: C.text3, lineHeight: 1.5, fontFamily: FONT_TXT }}>
                      {item.desc}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

        </Box>
      </Box>

      {/* MODAL CONFIRMAÇÃO DE EXCLUSÃO */}
      <Dialog
        open={!!deleteConfirm}
        onClose={() => !deleting && setDeleteConfirm(null)}
        PaperProps={{ sx: { borderRadius: '10px', maxWidth: 460 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: C.text, pb: 1, fontFamily: FONT_TXT }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
            <FiTrash2 size={18} color={C.red} />
            <span>Excluir chip?</span>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: C.text2, mb: 2, fontFamily: FONT_TXT, fontSize: '0.85rem' }}>
            Você está prestes a excluir o chip{' '}
            <strong>{deleteConfirm?.name}</strong>{' '}
            (<code style={{ background: C.inset, padding: '2px 6px', borderRadius: 4 }}>+{deleteConfirm?.phone_number}</code>).
          </Typography>
          <Alert severity="warning" sx={{ borderRadius: '6px', fontSize: '0.78rem' }}>
            As conversas vinculadas a esse número saem da listagem, mas o histórico
            permanece no banco para auditoria. O slot do plano é liberado.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setDeleteConfirm(null)} disabled={deleting} sx={{ textTransform: 'none', color: C.text2 }}>
            Cancelar
          </Button>
          <Button
            variant="contained" color="error"
            onClick={handleDeleteDevice}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={13} color="inherit" /> : <FiTrash2 size={13} />}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '6px', px: 2.5 }}
          >
            {deleting ? 'Excluindo...' : 'Excluir chip'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─── CTA styles (compartilhados pelos botões dos cards) ─────────────────────
const ctaPrimary = {
  display: 'inline-flex', alignItems: 'center', gap: 0.5,
  px: 1.5, py: 0.9, borderRadius: '6px',
  fontFamily: FONT_TXT, fontSize: '0.7rem', fontWeight: 700,
  cursor: 'pointer', border: 'none',
  letterSpacing: '0.02em', whiteSpace: 'nowrap',
  bgcolor: C.green, color: '#fff',
  boxShadow: '0 2px 6px rgba(0,184,101,0.25)',
  transition: 'all 0.15s',
  '&:hover': {
    bgcolor: '#00a558',
    boxShadow: '0 4px 12px rgba(0,184,101,0.3)',
  }
};
const ctaStyle = (bg, color, border) => ({
  display: 'inline-flex', alignItems: 'center', gap: 0.5,
  px: 1.5, py: 0.9, borderRadius: '6px',
  fontFamily: FONT_TXT, fontSize: '0.7rem', fontWeight: 700,
  cursor: 'pointer',
  letterSpacing: '0.02em', whiteSpace: 'nowrap',
  bgcolor: bg, color, border: `1px solid ${border}`,
  transition: 'all 0.15s',
  '&:hover': { color: C.text, borderColor: C.bdr2 }
});
